import { useState, useEffect } from 'react'
import { coursesStructure } from '../data/courseData'
import { coursesData } from '../data/coursesData'
import {
  CheckCircleIcon,
  StarIcon,
  AwardIcon,
  TrophyIcon,
  ShieldCheckIcon,
  ZapIcon,
  TargetIcon,
} from 'lucide-react'

interface LessonProgress {
  courseId: string
  moduleId: string
  lessonId: string
  completedAt: string
}

interface PointsBreakdown {
  lessonPoints: number
  moduleBonus: number
  courseBonus: number
  totalPoints: number
}

export interface Badge {
  id: string
  courseId: string
  moduleId: string
  moduleName: string
  courseName: string
  earnedAt?: string
  isEarned: boolean
  icon: any
  gradient: string
  description: string
}

export interface Certificate {
  id: string
  courseId: string
  courseName: string
  earnedAt?: string
  isEarned: boolean
  totalModules: number
  totalLessons: number
  icon: any
  gradient: string
  description: string
}

export function useProgress() {
  const [completedLessons, setCompletedLessons] = useState<LessonProgress[]>([])

  useEffect(() => {
    const stored = localStorage.getItem('clc-lesson-progress')
    if (stored) {
      try {
        setCompletedLessons(JSON.parse(stored))
      } catch (e) {
        console.error('Error loading progress:', e)
      }
    }
  }, [])

  const markLessonComplete = (
    courseId: string,
    moduleId: string,
    lessonId: string,
  ) => {
    const newProgress: LessonProgress = {
      courseId,
      moduleId,
      lessonId,
      completedAt: new Date().toISOString(),
    }
    const updated = [
      ...completedLessons.filter(
        (p) =>
          !(
            p.courseId === courseId &&
            p.moduleId === moduleId &&
            p.lessonId === lessonId
          ),
      ),
      newProgress,
    ]
    setCompletedLessons(updated)
    localStorage.setItem('clc-lesson-progress', JSON.stringify(updated))
  }

  const isLessonComplete = (
    courseId: string,
    moduleId: string,
    lessonId: string,
  ): boolean => {
    return completedLessons.some(
      (p) =>
        p.courseId === courseId &&
        p.moduleId === moduleId &&
        p.lessonId === lessonId,
    )
  }

  const getCourseProgress = (
    courseId: string,
    totalLessons: number,
  ): number => {
    const completedCount = completedLessons.filter(
      (p) => p.courseId === courseId,
    ).length
    return totalLessons > 0
      ? Math.round((completedCount / totalLessons) * 100)
      : 0
  }

  const getModuleProgress = (
    courseId: string,
    moduleId: string,
    totalLessons: number,
  ): number => {
    const completedCount = completedLessons.filter(
      (p) => p.courseId === courseId && p.moduleId === moduleId,
    ).length
    return totalLessons > 0
      ? Math.round((completedCount / totalLessons) * 100)
      : 0
  }

  const getOverallProgress = (): {
    completedLessons: number
    totalLessons: number
  } => {
    const completedCount = completedLessons.length
    const totalLessons = 21 // This should ideally be calculated dynamically

    return {
      completedLessons: completedCount,
      totalLessons: totalLessons,
    }
  }

  // Check if a module is complete
  const isModuleComplete = (courseId: string, moduleId: string): boolean => {
    const course = coursesStructure.find((c) => c.id === courseId)
    if (!course) return false

    const module = course.modules.find((m) => m.id === moduleId)
    if (!module) return false

    const totalLessons = module.lessons.length
    const completedCount = completedLessons.filter(
      (p) => p.courseId === courseId && p.moduleId === moduleId,
    ).length

    return completedCount === totalLessons && totalLessons > 0
  }

  // Check if a course is complete
  const isCourseComplete = (courseId: string): boolean => {
    const course = coursesStructure.find((c) => c.id === courseId)
    if (!course) return false

    for (const module of course.modules) {
      if (!isModuleComplete(courseId, module.id)) {
        return false
      }
    }

    return true
  }

  // Calculate total points with breakdown
  const calculatePoints = (): PointsBreakdown => {
    let lessonPoints = 0
    let moduleBonus = 0
    let courseBonus = 0

    // 10 points per completed lesson
    lessonPoints = completedLessons.length * 10

    // 25 points bonus per completed module
    const completedModules = new Set<string>()
    coursesStructure.forEach((course) => {
      course.modules.forEach((module) => {
        if (isModuleComplete(course.id, module.id)) {
          completedModules.add(`${course.id}-${module.id}`)
        }
      })
    })
    moduleBonus = completedModules.size * 25

    // 100 points bonus per completed course
    const completedCourses = coursesStructure.filter((course) =>
      isCourseComplete(course.id),
    ).length
    courseBonus = completedCourses * 100

    return {
      lessonPoints,
      moduleBonus,
      courseBonus,
      totalPoints: lessonPoints + moduleBonus + courseBonus,
    }
  }

  // Get points for a specific course
  const getCoursePoints = (courseId: string): PointsBreakdown => {
    const course = coursesStructure.find((c) => c.id === courseId)
    if (!course) {
      return { lessonPoints: 0, moduleBonus: 0, courseBonus: 0, totalPoints: 0 }
    }

    let lessonPoints = 0
    let moduleBonus = 0
    let courseBonus = 0

    // Count lessons in this course
    const courseLessons = completedLessons.filter(
      (p) => p.courseId === courseId,
    )
    lessonPoints = courseLessons.length * 10

    // Count completed modules in this course
    let completedModulesCount = 0
    course.modules.forEach((module) => {
      if (isModuleComplete(courseId, module.id)) {
        completedModulesCount++
      }
    })
    moduleBonus = completedModulesCount * 25

    // Check if course is complete
    if (isCourseComplete(courseId)) {
      courseBonus = 100
    }

    return {
      lessonPoints,
      moduleBonus,
      courseBonus,
      totalPoints: lessonPoints + moduleBonus + courseBonus,
    }
  }

  // Helper to get gradient based on course ID
  const getCourseGradient = (courseId: string): string => {
    switch (courseId) {
      case 'concept-clc':
        return 'from-blue-500 to-indigo-600'
      case 'proceduri-inchiriere':
        return 'from-emerald-500 to-teal-600'
      case 'proceduri-vanzare':
        return 'from-orange-500 to-red-600'
      default:
        return 'from-slate-500 to-gray-600'
    }
  }

  // Helper to get icon based on module/course index
  const getBadgeIcon = (index: number) => {
    const icons = [
      CheckCircleIcon,
      StarIcon,
      AwardIcon,
      ShieldCheckIcon,
      ZapIcon,
      TargetIcon,
    ]
    return icons[index % icons.length]
  }

  // Get all badges (earned + locked)
  const getAllBadges = (): Badge[] => {
    const badges: Badge[] = []

    coursesStructure.forEach((course) => {
      const courseInfo = coursesData.find((c) => c.id === course.id)
      const courseName = courseInfo?.title || course.id
      const gradient = getCourseGradient(course.id)

      course.modules.forEach((module, index) => {
        const isEarned = isModuleComplete(course.id, module.id)

        // Find completion date if earned (approximate by last lesson completion)
        let earnedAt: string | undefined
        if (isEarned) {
          const moduleLessons = completedLessons.filter(
            (p) => p.courseId === course.id && p.moduleId === module.id,
          )
          // Sort by date and get the last one
          if (moduleLessons.length > 0) {
            moduleLessons.sort(
              (a, b) =>
                new Date(b.completedAt).getTime() -
                new Date(a.completedAt).getTime(),
            )
            earnedAt = moduleLessons[0].completedAt
          } else {
            earnedAt = new Date().toISOString() // Fallback
          }
        }

        badges.push({
          id: `${course.id}-${module.id}`,
          courseId: course.id,
          moduleId: module.id,
          moduleName: module.title,
          courseName: courseName,
          earnedAt,
          isEarned,
          icon: getBadgeIcon(index),
          gradient,
          description: `Insignă acordată pentru finalizarea modulului "${module.title}" din cursul "${courseName}".`,
        })
      })
    })

    return badges
  }

  // Get all certificates (earned + locked)
  const getAllCertificates = (): Certificate[] => {
    const certificates: Certificate[] = []

    coursesStructure.forEach((course) => {
      const courseInfo = coursesData.find((c) => c.id === course.id)
      const courseName = courseInfo?.title || course.id
      const gradient = getCourseGradient(course.id)
      const isEarned = isCourseComplete(course.id)

      let totalLessons = 0
      course.modules.forEach((m) => (totalLessons += m.lessons.length))

      // Find completion date if earned
      let earnedAt: string | undefined
      if (isEarned) {
        const courseLessons = completedLessons.filter(
          (p) => p.courseId === course.id,
        )
        if (courseLessons.length > 0) {
          courseLessons.sort(
            (a, b) =>
              new Date(b.completedAt).getTime() -
              new Date(a.completedAt).getTime(),
          )
          earnedAt = courseLessons[0].completedAt
        } else {
          earnedAt = new Date().toISOString()
        }
      }

      certificates.push({
        id: `cert-${course.id}`,
        courseId: course.id,
        courseName: courseName,
        earnedAt,
        isEarned,
        totalModules: course.modules.length,
        totalLessons,
        icon: TrophyIcon,
        gradient,
        description: `Certificat oficial de absolvire pentru cursul "${courseName}".`,
      })
    })

    return certificates
  }

  return {
    markLessonComplete,
    isLessonComplete,
    getCourseProgress,
    getModuleProgress,
    getOverallProgress,
    completedLessons,
    isModuleComplete,
    isCourseComplete,
    calculatePoints,
    getCoursePoints,
    getAllBadges,
    getAllCertificates,
  }
}
